import { supabase } from './supabase';
import { syncManager } from './syncManager';
import { accountGeneration } from './accountStorage';
import { CONSENT_DOCUMENT_VERSION, type ConsentKind } from '../constants/onboarding';

/**
 * IO for the onboarding sequence: `user_onboarding` (current state, one row per
 * user, updated in place) and `user_consents` (an append-only ledger).
 * Migration 0255.
 *
 * Follows `lib/userPreferences.ts` deliberately — same partial-upsert shape,
 * same fire-and-forget error handling. A failed write here must never take the
 * sequence down: someone answering thirteen screens on a bad connection should
 * lose a resume point, not their place.
 *
 * ⚠️⚠️ The writes take `at` — the caller's `accountGeneration()` from when the person
 * acted. Each asks who is signed in when its turn comes, and a sign-out does not
 * reload the page: asked as the account changes, the answer is the NEXT account, and
 * this account's step or consents would be written as that one's. Once the account
 * has moved they write nothing.
 */

const ONBOARDING_TABLE = 'user_onboarding';
const CONSENTS_TABLE = 'user_consents';

export interface UserOnboardingRow {
  user_id: string;
  /* ⚠️ `reasons` is the ONLY answer stored here. Everything else the sequence
     collects already has a home: name → `user_profiles.name`, full name and
     date of birth → `userStore` (→ `user_identity.data`, edited in Settings →
     Personal Details), gender → `user_preferences.body_figure_gender`, consents
     → the `user_consents` ledger. A column here for any of them would be a
     second record of one fact, wrong from the first time it is edited. */
  reasons?: string[] | null;
  current_step?: number | null;
  completed_at?: string | null;
}

export interface UserConsentRow {
  kind: ConsentKind;
  granted: boolean;
  document_version: string;
  source: string;
  created_at: string;
}

export function fetchOnboarding(): Promise<
  { userId: string; row: UserOnboardingRow | null } | null
> {
  return doFetch();
}

async function doFetch(): Promise<{ userId: string; row: UserOnboardingRow | null } | null> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return null;
    const { data, error } = await supabase.from(ONBOARDING_TABLE).select('*').limit(1);
    if (error) {
      console.error('Failed to load onboarding:', error);
      return { userId: user.id, row: null };
    }
    return { userId: user.id, row: (data ?? [])[0] ?? null };
  } catch (error) {
    console.error('Error loading onboarding:', error);
    return null;
  }
}

/** Upsert just the provided columns. Fire-and-forget. `at`: see the note at the top. */
export async function saveOnboarding(
  partial: Partial<Omit<UserOnboardingRow, 'user_id'>>,
  at: number,
): Promise<void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== accountGeneration()) return;
    const { error } = await supabase
      .from(ONBOARDING_TABLE)
      .upsert({ user_id: user.id, ...partial }, { onConflict: 'user_id' });
    if (error) console.error('Failed to save onboarding:', error);
  } catch (error) {
    console.error('Error saving onboarding:', error);
  }
}

/**
 * Append consent records — one row per answer, never an update.
 *
 * ⚠️⚠️ INSERT, NOT UPSERT, AND THAT IS THE POINT. The table has no UPDATE or
 * DELETE policy, so an upsert here would fail at the database rather than
 * quietly rewriting history — but writing it as an insert is what makes the
 * intent readable. If someone accepts in August and withdraws in October, both
 * rows survive in order, which is the only way to answer "what had they agreed
 * to when we processed that data?".
 *
 * ⚠️ Returns whether the write landed. Consent is the one thing in this
 * sequence a caller may want to know actually persisted — unlike a resume
 * point, it is evidence.
 *
 * ⚠️⚠️ `at` (see the note at the top) — above all here: the ledger cannot be
 * edited, so one account's answers entered as the next one's would stand as
 * what that person agreed to.
 */
export async function recordConsents(
  entries: readonly { kind: ConsentKind; granted: boolean }[],
  at: number,
  source = 'onboarding',
): Promise<boolean> {
  if (entries.length === 0) return true;
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== accountGeneration()) return false;
    const { error } = await supabase.from(CONSENTS_TABLE).insert(
      entries.map((e) => ({
        user_id: user.id,
        kind: e.kind,
        granted: e.granted,
        document_version: CONSENT_DOCUMENT_VERSION,
        source,
      })),
    );
    if (error) {
      console.error('Failed to record consents:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error recording consents:', error);
    return false;
  }
}

/**
 * Every consent answer this user has ever given, newest first.
 *
 * ⚠️ Returns the FULL ledger, not the current position — deciding which row is
 * current is the caller's job, because "newest per kind" is the answer for a
 * settings screen and "what applied on date X" is the answer for an audit, and
 * collapsing here would throw away the second one.
 */
export async function fetchConsentHistory(): Promise<UserConsentRow[]> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return [];
    const { data, error } = await supabase
      .from(CONSENTS_TABLE)
      .select('kind, granted, document_version, source, created_at')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Failed to load consents:', error);
      return [];
    }
    return (data ?? []) as UserConsentRow[];
  } catch (error) {
    console.error('Error loading consents:', error);
    return [];
  }
}

/** Newest answer per kind — the current position. */
export function currentConsents(
  history: readonly UserConsentRow[],
): Partial<Record<ConsentKind, boolean>> {
  const out: Partial<Record<ConsentKind, boolean>> = {};
  // ⚠️ History arrives newest-first, so the FIRST row seen for a kind wins and
  // later (older) rows must not overwrite it.
  for (const row of history) {
    if (!(row.kind in out)) out[row.kind] = row.granted;
  }
  return out;
}
